// Attribution — cross-session ad-click capture.
//
// Problem: a visitor clicks a Facebook / TikTok / Google ad, lands on
// calofay.app with `?fbclid=...&utm_source=facebook&...`, bounces,
// manually installs the app from the App Store 2 days later, and
// subscribes. Without a persistent click-ID link, Meta Ads Manager
// never credits that purchase back to the ad spend that actually
// produced it — ROAS looks ~40% worse than it is, so Meta's auto-
// optimisation keeps feeding budget to the wrong creatives.
//
// Fix: on EVERY landing page hit, harvest the click IDs + UTMs + Meta
// browser cookies (`_fbp`, `_fbc`) into localStorage with a 90-day TTL.
// Every subsequent funnel step (signup, checkout, app login after
// install) pulls the saved blob and ships it to the backend, which
// then forwards it to Meta's Conversions API on purchase. Same-email
// native app installs reuse the saved click IDs via the signup POST.

const LS_KEY = "calofay_attribution_v1";
const TTL_MS = 90 * 24 * 60 * 60 * 1000; // 90 days — Meta's own CAPI window

const CLICK_PARAMS = [
  "fbclid", "ttclid", "gclid", "gbraid", "wbraid", "msclkid",
];
const UTM_PARAMS = [
  "utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term",
];

function _readCookie(name) {
  try {
    const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : null;
  } catch { return null; }
}

/** Build Meta's `_fbc` cookie value from a raw `fbclid`.
 *  Format: `fb.<subdomain-index>.<unix-ts-ms>.<fbclid>`.
 *  Meta's docs recommend fabricating this on the server if the real
 *  cookie wasn't set in time; we do it client-side so later
 *  server-side CAPI calls get a non-null `fbc` even for first-touch
 *  bounces. */
function _synthFbc(fbclid) {
  if (!fbclid) return null;
  return `fb.1.${Date.now()}.${fbclid}`;
}

/** Capture attribution signals from the current URL + browser cookies
 *  and merge into localStorage. Idempotent — repeat calls only
 *  overwrite fields that are present on this hit, so navigating
 *  calofay.app → /quiz → /checkout never clobbers the fbclid captured
 *  on the first landing. Returns the merged attribution blob. */
export function captureAttribution() {
  if (typeof window === "undefined") return null;
  const now = Date.now();
  const incoming = {};
  try {
    const params = new URLSearchParams(window.location.search);
    for (const p of CLICK_PARAMS) {
      const v = params.get(p);
      if (v) incoming[p] = v;
    }
    for (const p of UTM_PARAMS) {
      const v = params.get(p);
      if (v) incoming[p] = v;
    }
  } catch { /* non-browser or malformed URL */ }

  // Meta pixel cookies — captured even without URL params (fbp is set
  // on first Pixel fire; fbc is set when fbclid lands).
  const fbp = _readCookie("_fbp");
  const fbc = _readCookie("_fbc") || _synthFbc(incoming.fbclid);
  if (fbp) incoming._fbp = fbp;
  if (fbc) incoming._fbc = fbc;

  // Landing context — useful for Meta's `event_source_url` + GA debug.
  try {
    incoming.landing_url = incoming.landing_url || window.location.href;
    incoming.referrer    = incoming.referrer    || document.referrer || null;
    incoming.user_agent  = incoming.user_agent  || navigator.userAgent || null;
  } catch { /* best-effort */ }

  // Merge-first: never overwrite a known fbclid with null on a later
  // pageview that lacks params. First-touch wins per field.
  let prev = {};
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && parsed.expires_at && parsed.expires_at > now) {
        prev = parsed.data || {};
      }
    }
  } catch { /* corrupt payload — start fresh */ }

  const merged = { ...prev };
  for (const [k, v] of Object.entries(incoming)) {
    if (v && !merged[k]) merged[k] = v; // first-touch wins
  }
  // Timestamps are always refreshed — Meta's CAPI uses `event_time`
  // not `click_time`, so this just tracks when WE last saw them.
  merged._last_seen_at = new Date(now).toISOString();
  if (!merged._first_seen_at) merged._first_seen_at = merged._last_seen_at;

  try {
    localStorage.setItem(LS_KEY, JSON.stringify({
      data: merged,
      expires_at: now + TTL_MS,
    }));
  } catch { /* storage full / disabled — attribution degrades gracefully */ }

  return merged;
}

/** Read the saved attribution blob without mutating it. Returns `null`
 *  if nothing was captured yet or the record expired. Downstream
 *  callers forward this to `/api/*` endpoints — we don't inline-fetch
 *  so the attribution surface stays trivially inspectable from
 *  devtools. */
export function readAttribution() {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.data) return null;
    if (parsed.expires_at && parsed.expires_at <= Date.now()) {
      localStorage.removeItem(LS_KEY);
      return null;
    }
    return parsed.data;
  } catch { return null; }
}

/** Returns a trimmed attribution payload safe to send in an API body.
 *  Keeps the Meta/Google click IDs + UTM set + fbp/fbc + event_source_url
 *  and drops the local-only timestamps. */
export function attributionForApi() {
  const a = readAttribution();
  if (!a) return null;
  const payload = {};
  for (const k of [...CLICK_PARAMS, ...UTM_PARAMS, "_fbp", "_fbc", "landing_url", "referrer", "user_agent"]) {
    if (a[k]) payload[k] = a[k];
  }
  return Object.keys(payload).length > 0 ? payload : null;
}

/** Clear the attribution record — call after confirmed purchase so a
 *  renewal 12 months later doesn't get re-attributed to the original
 *  ad click. */
export function clearAttribution() {
  try { localStorage.removeItem(LS_KEY); } catch { /* ignore */ }
}
